import { skipToken, type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback, useEffect } from "react";
import { SCHEDULE_CHANGES_QUERY_KEY_ROOT, SCHEDULE_CHANGES_REFETCH_INTERVAL_MS, SCHEDULE_CHANGES_STALE_TIME_MS } from "../components/scheduleChanges/consts";
import { parseScheduleChangeItem, toCachedScheduleChangeItem, toDepartmentDateTime } from "../components/scheduleChanges/parseScheduleChanges";
import type { ScheduleChangeItemCached, ScheduleChangesFeed, ScheduleChangesWindow } from "../components/scheduleChanges/types";
import { requestBounds, resolveWindowBounds, type WindowBounds, windowKeyParts } from "../components/scheduleChanges/windowBounds";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { backendService } from "../services/backend.service";
import { retryUnlessForbidden } from "../utils/queryRetry";
import { useLatestRef } from "./useLatestRef";

/**
 * What the cache holds: the response as plain JSON (see `ScheduleChangeItemCached`), and the start the request actually
 * sent (after trimming). The trim is rounded to a day (`trimToServerWindow`), so polls with the same answer cache an
 * equal payload.
 */
type ScheduleChangesFeedCache = {
	items: ScheduleChangeItemCached[];
	sentSinceIso: string;
};

export type ScheduleChangesFeedResult = {
	feed: ScheduleChangesFeed;
	/** Where the data starts: the window's start, or later when the window was longer than the server answers. */
	since: DateTime;
};

const fetchFeed = async (departmentId: string, bounds: WindowBounds): Promise<ScheduleChangesFeedCache> => {
	if (bounds.status === "invalid") throw bounds.error;
	if (bounds.status === "none") throw new Error("schedule changes: fetched without a window");
	const { sinceIso, untilIso } = requestBounds(bounds.bounds);
	const { data } = await backendService.getScheduleChangesFeed(departmentId, sinceIso, untilIso);
	return {
		items: data.items.map(toCachedScheduleChangeItem),
		sentSinceIso: sinceIso,
	};
};

/**
 * Every schedule change in the chosen department inside `changesWindow` — by the viewer (`actorKind: "me"`), other
 * people and the system — newest-first as the server sends them, with every datetime in the department's timezone,
 * and how many changes only the activity log can show. Pass `null` to not ask (the user is staff, or no window yet).
 *
 * Read it through `useScheduleChanges()`: the provider asks once and the Changes tab and the bell's unseen dot share that
 * answer. Call this directly only for a window other than the shared one.
 *
 * Asks only while `isShowing` — something on screen shows the changes. Then it fetches (unless the cached answer is
 * still fresh) and polls every 5 minutes while the tab is visible, and on focus; otherwise it asks nothing and keeps
 * the last answer for the next surface that shows it.
 *
 * The cache keeps the wire data and `select` parses it, so a poll that brings back the same feed is structurally equal
 * and no consumer re-renders.
 *
 * A failed load (including a window with an invalid start) is tracked as `schedule_changes_feed_fetch_error` and
 * returned as the query's error state; the caller renders it.
 */
export const useScheduleChangesFeed = (changesWindow: ScheduleChangesWindow | null, isShowing: boolean): UseQueryResult<ScheduleChangesFeedResult, Error> => {
	const { chosenDepartment } = useChosenDepartment();
	const { trackError } = useAnalytics();
	// Read in the effect, not a dependency: a new `trackError` must not report the same failure twice.
	const trackErrorRef = useLatestRef(trackError);
	const departmentId = chosenDepartment.department_id;
	const timezone = chosenDepartment.timezone;
	const bounds = resolveWindowBounds(changesWindow);
	// The placeholder department has empty ids until the real one loads.
	const canFetch = bounds.status !== "none" && departmentId !== "" && timezone !== "";

	// Stable per zone, so TanStack re-runs it only when the cached data itself changes.
	const select = useCallback(
		(cache: ScheduleChangesFeedCache): ScheduleChangesFeedResult => ({
			feed: { items: cache.items.map((item) => parseScheduleChangeItem(item, timezone)) },
			since: toDepartmentDateTime(cache.sentSinceIso, timezone, "since"),
		}),
		[timezone],
	);

	const query = useQuery<ScheduleChangesFeedCache, Error, ScheduleChangesFeedResult>({
		queryKey: [SCHEDULE_CHANGES_QUERY_KEY_ROOT, "feed", departmentId, timezone, ...windowKeyParts(bounds)],
		queryFn: canFetch ? () => fetchFeed(departmentId, bounds) : skipToken,
		select,
		enabled: isShowing,
		staleTime: SCHEDULE_CHANGES_STALE_TIME_MS,
		refetchInterval: SCHEDULE_CHANGES_REFETCH_INTERVAL_MS,
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
		// An invalid window fails the same way every time: show the error now rather than after the retries.
		retry: (failureCount, error) => bounds.status === "valid" && retryUnlessForbidden(failureCount, error),
	});

	useEffect(() => {
		if (query.isError) {
			trackErrorRef.current("schedule_changes_feed_fetch_error", { department_id: departmentId, error_message: query.error.message });
		}
	}, [query.isError, query.error, departmentId, trackErrorRef]);

	return query;
};
